import { useCallback, useEffect, useRef, useState } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { InfiniteQueryResult } from '@frontend/types';
import { SchedulePulseTab, useSchedulePulseShallowStore } from '../../../../../../stores/schedule-pulse.store';

type UseOpenedRequestProps = {
  locationId: string | undefined;
  infiniteListQuery: InfiniteQueryResult<Record<'rows', ScheduleRequest[]>>;
};

export const useOpenedRequest = ({ locationId, infiniteListQuery }: UseOpenedRequestProps) => {
  const { isLoading, data, fetchNextPage, hasNextPage } = infiniteListQuery;
  const { selectedRequestLocationId, selectedRequestId, setSelectedRequestId, selectedTab } =
    useSchedulePulseShallowStore(
      'selectedRequestLocationId',
      'selectedRequestId',
      'setSelectedRequestId',
      'selectedTab'
    );
  const isMounted = useRef(false);
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);

  useEffect(() => {
    if (isMounted.current) {
      if (
        selectedRequestLocationId === locationId &&
        selectedRequestId &&
        selectedTab === SchedulePulseTab.AppointmentRequests
      ) {
        setSelectedRequest(selectedRequestId);
      }
    } else {
      isMounted.current = true;
    }
  }, [selectedRequestLocationId, selectedRequestId, locationId, selectedTab]);

  useEffect(() => {
    if (!isLoading && selectedRequestId) {
      const requestLoaded = data?.pages
        ?.flatMap((page) => page.rows)
        ?.find((request) => request.id === selectedRequestId);
      if (!requestLoaded && hasNextPage) {
        fetchNextPage();
      }
    }
  }, [isLoading, data, selectedRequestId, fetchNextPage, hasNextPage]);

  const setSelectedRequestDetails = useCallback(
    (requestId: string | null) => {
      if (requestId) {
        setSelectedRequest(requestId);
      } else {
        setSelectedRequest(null);
      }
      setSelectedRequestId(null);
    },
    [setSelectedRequestId]
  );

  return { selectedRequest, setSelectedRequest: setSelectedRequestDetails };
};
